/**
 * HistoryItem - Represents a single chat history item (prompt + optional response)
 *
 * This component is responsible for:
 * - Pairing a prompt with its execution and response
 * - Managing the relationship between prompt, execution, and response data
 * - Delegating presentation to PromptBubble and ResponseBubble
 */

import PromptBubble from './PromptBubble';
import ResponseBubble from './ResponseBubble';

interface HistoryItemProps {
  // Prompt data
  promptId: string;
  promptText: string;
  promptCreatedAt: string | Date; // Timestamp from server (ISO UTC string or Date object)

  // Execution data (optional - may not exist yet)
  execution?: {
    id: string;
    status: 'pending' | 'running' | 'retrying' | 'failed' | 'completed';
    createdAt: string | Date; // Timestamp (ISO UTC string or Date object)
    startedAt?: string | Date; // Timestamp - set when execution starts
    completedAt?: string | Date; // Timestamp - set when execution completes
    errorMessage?: string;
    agent?: string;
    metadata?: {
      model?: string;
      provider?: string;
      inputTokens?: number;
      outputTokens?: number;
      costUsd?: number;
    };
  };

  // Response data (optional - only exists when execution completes)
  response?: {
    text: string;
    createdAt: string | Date; // Timestamp (ISO UTC string or Date object)
  };

  // Actions
  onRetry?: (promptId: string, executionId: string) => void;
}

export default function HistoryItem({
  promptId,
  promptText,
  promptCreatedAt,
  execution,
  response,
  onRetry,
}: HistoryItemProps) {

  // Parse timestamps - handles both ISO strings and Date objects
  const parseUTC = (timestamp: string | Date | undefined): Date => {
    if (!timestamp) {
      return new Date();
    }

    // If already a Date object, return as-is
    if (timestamp instanceof Date) {
      return timestamp;
    }

    // If string, parse as UTC
    const str = String(timestamp);
    if (!str.endsWith('Z')) {
      return new Date(str + 'Z');
    }
    return new Date(str);
  };

  // Prepare data for PromptBubble
  const promptTimestamp = parseUTC(promptCreatedAt);
  const executionCreatedAt = execution?.createdAt
    ? parseUTC(execution.createdAt)
    : undefined;
  const executionStartedAt = execution?.startedAt
    ? parseUTC(execution.startedAt)
    : (execution?.status === 'running' ? parseUTC(execution.createdAt) : undefined);
  const executionCompletedAt = execution?.completedAt
    ? parseUTC(execution.completedAt)
    : undefined;

  // Can retry if execution failed
  const canRetry = execution?.status === 'failed';

  return (
    <>
      <PromptBubble
        content={promptText}
        timestamp={promptTimestamp}
        executionStatus={execution?.status}
        errorMessage={execution?.errorMessage}
        canRetry={canRetry}
        onRetry={canRetry && onRetry ? () => onRetry(promptId, execution!.id) : undefined}
        hasExecution={!!execution}
        inputTokens={execution?.metadata?.inputTokens}
        outputTokens={execution?.metadata?.outputTokens}
        createdAt={executionCreatedAt}
        startedAt={executionStartedAt}
        completedAt={executionCompletedAt}
        agentName={execution?.agent}
        modelName={execution?.metadata?.model}
        provider={execution?.metadata?.provider}
        costUsd={execution?.metadata?.costUsd}
      />

      {response && execution?.status === 'completed' && (
        <ResponseBubble
          message={{
            id: `${promptId}-response`,
            role: 'assistant',
            content: response.text,
            timestamp: parseUTC(response.createdAt),
            status: 'sent',
            agentName: execution?.agent || execution?.metadata?.model,
            promptId,
            executionId: execution?.id,
            executionStatus: 'completed',
          }}
        />
      )}
    </>
  );
}
